// ActionStrip — the "Take action" row between the holdings overview and the
// holdings tabs, matching the Participant Redesign standalone (portfolio3.jsx
// ActionStrip).
//
// Per the 8/4 review the Portfolio page no longer carries individual transactional
// pills (Rebalance / Transfer / Change elections). It offers one account action —
// Manage account — alongside two read-only ways to look deeper: transaction history
// and rate of return. Everything transactional lives behind Manage Account.
//
// Gating follows the design's rule exactly: an action a plan doesn't grant is
// simply not rendered. No disabled buttons, no dead ends. So:
//   • Manage appears when the plan lets the participant CHANGE something.
//   • Rate of return appears only where a personal ROR exists (a credited-rate
//     plan doesn't report one).
// Pure/presentational — the page supplies the destinations. V3 has no write surface
// yet, so a page can pass `portalHref` to send EVERY pill to the participant portal
// (a new tab) instead of an internal route or a handler.
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SlidersIcon, ClockIcon, TrendIcon, ArrowRightIcon } from '../../../icons/Icons';
import './action-strip.css';

/**
 * Permissions that mean the participant has a DECISION to make on the Manage page.
 *
 * Statements and beneficiaries are deliberately absent. A plan granting only those
 * two is servicing-only — the employer directs the money and there is nothing about
 * contributions or investing for the participant to change — so "Manage account"
 * next to "Take action" would promise an action this plan doesn't offer. A Penny
 * Saved (the cash-balance plan) is exactly that case. Manage is still reachable
 * from the top nav, so no capability becomes unreachable.
 *
 * These flags alone are NOT enough to recognise that case against the real backend:
 * /portfolio ships no permissions yet, so the adapter defaults them to granted (it
 * has to — defaulting to false would strip the Manage link off every live account).
 * `employerDirected` carries the plan-type signal that the flags can't, and it wins
 * over them.
 */
const MANAGEABLE = [
  'canChangeRate',
  'canAutoIncrease',
  'canChangeElections',
  'canRollover',
  'canDistribute',
  'canRebalance',
  'canTransfer',
  'canRequestLoan',
];

/**
 * One pill. Renders as an external anchor for `href` (the portal hand-off), an
 * internal <Link> for `to`, and a plain button otherwise — same markup and classes in
 * all three, so the row looks identical however the page wires it.
 */
function Pill({
  icon: Icon, full, short, href, to, onClick, onHrefClick,
}) {
  const inner = (
    <>
      <Icon className="action-strip__icon" width={13} height={13} />
      <span className="action-strip__text">
        <span className="action-strip__text-full">{full}</span>
        <span className="action-strip__text-short">{short}</span>
      </span>
      <ArrowRightIcon className="action-strip__arrow" width={13} height={13} />
    </>
  );

  if (href) {
    // A real anchor, so middle-click / "open in new tab" / screen readers all behave.
    // `onHrefClick` (when the page supplies one) takes the PLAIN left-click instead, so
    // an embedded parent can navigate its own tab; modified clicks fall through to the
    // browser's own new-tab handling.
    const intercept = onHrefClick
      ? (e) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        onHrefClick();
      }
      : undefined;
    return (
      <a
        className="action-strip__pill"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={intercept}
      >
        {inner}
      </a>
    );
  }
  if (to) return <Link className="action-strip__pill" to={to}>{inner}</Link>;
  return <button type="button" className="action-strip__pill" onClick={onClick}>{inner}</button>;
}

Pill.propTypes = {
  icon: PropTypes.elementType.isRequired,
  full: PropTypes.node.isRequired,
  short: PropTypes.node.isRequired,
  /** External destination — wins over `to`/`onClick`; opens in a new tab. */
  href: PropTypes.string,
  to: PropTypes.string,
  onClick: PropTypes.func,
  /** Takes the plain left-click on an `href` pill (modified clicks still open a tab). */
  onHrefClick: PropTypes.func,
};

export function ActionStrip({
  actions = {},
  employerDirected = false,
  manageTo = '/manage',
  historyTo,
  showRor = true,
  onHistory,
  onRor,
  portalHref,
  onPortalNavigate,
}) {
  const { t } = useTranslation();
  const canManage = !employerDirected && MANAGEABLE.some((f) => actions[f] === true);
  // `portalHref` may be a plain URL (every pill shares it) or a resolver the page uses
  // to give each pill its own legacy route — see hooks/useLegacyPortal.
  const hrefFor = (key) => (typeof portalHref === 'function' ? portalHref(key) : portalHref);
  const goTo = (key) => (portalHref && onPortalNavigate ? () => onPortalNavigate(key) : undefined);

  // Nothing granted and nothing to look at → the row itself has no reason to exist.
  // `portalHref` counts as somewhere to go: it gives every pill a destination.
  if (!canManage && !showRor && !historyTo && !onHistory && !portalHref) return null;

  return (
    <section className="action-strip" aria-label={t('portfolio.actions.label')}>
      <span className="action-strip__label">{t('portfolio.actions.label')}</span>

      {canManage && (
        <Pill
          icon={SlidersIcon}
          full={t('portfolio.actions.manage')}
          short={t('portfolio.actions.manageShort')}
          href={hrefFor('manage')}
          onHrefClick={goTo('manage')}
          to={portalHref ? undefined : manageTo}
        />
      )}

      <Pill
        icon={ClockIcon}
        full={t('portfolio.actions.history')}
        short={t('portfolio.actions.historyShort')}
        href={hrefFor('history')}
        onHrefClick={goTo('history')}
        onClick={onHistory}
      />

      {showRor && (
        <Pill
          icon={TrendIcon}
          full={t('portfolio.actions.ror')}
          short={t('portfolio.actions.rorShort')}
          href={hrefFor('ror')}
          onHrefClick={goTo('ror')}
          onClick={onRor}
        />
      )}

    </section>
  );
}

ActionStrip.propTypes = {
  /** Participant permission flags for this plan (adapter: `data.actions`). */
  actions: PropTypes.object,
  /**
   * True where the plan type itself gives the participant nothing to direct (cash
   * balance / guaranteed rate). Hides Manage regardless of the permission flags.
   */
  employerDirected: PropTypes.bool,
  /** Manage Account route. */
  manageTo: PropTypes.string,
  /** Optional transaction-history route (falls back to the onHistory handler). */
  historyTo: PropTypes.string,
  /** False on credited-rate plans, which report no personal rate of return. */
  showRor: PropTypes.bool,
  onHistory: PropTypes.func,
  onRor: PropTypes.func,
  /**
   * Legacy-portal destination. When set, EVERY pill becomes an external link and
   * `manageTo` / `onHistory` / `onRor` are ignored — the hand-off used while V3 is
   * read-only. Either one URL for all three pills, or a `(routeKey) => url` resolver
   * (`useLegacyPortal().legacyHref`) so each pill gets its own legacy page.
   */
  portalHref: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  /**
   * Called with the pill's route key on a plain left-click, instead of following the
   * href — lets an embedded page hand off via the parent window rather than a new tab.
   */
  onPortalNavigate: PropTypes.func,
};

export default ActionStrip;
